import strings from '../../../strings/strings.json';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { sizes, breakpoints, theme } from 'theme';
import CourseBuilderValidationWarning from './CourseBuilderValidationWarning';
import { Dispatch, SetStateAction } from 'react';
import React from 'react';
import { BuildCourseContext } from 'contextProviders/BuildCourseContext';
import Alert from 'components/alert/Alert';
import LogoutIcon from 'assets/icons/navigation/LogoutIcon';
import PreviewIcon from 'assets/icons/controls/PreviewIcon';
import { IconSize } from 'assets/icons/icon-sizes';
import { AuthContext } from 'contextProviders/AuthContext';
import { RoleType } from 'models/User';
import { FlexColMd } from 'components/Elements';
import VisibilityIcon from 'assets/icons/accessibility/VisibilityIcon';
import Button from 'components/buttons/Button';

type CourseEditorButtonsProps = {
    courseId: string;
    courseReadyToPublish: boolean;
    showPublishWarning: boolean;
    setShowPublishWarning: Dispatch<SetStateAction<boolean>>;
    setShowPublishModal: Dispatch<SetStateAction<boolean>>;
    setShowReviewModal: Dispatch<SetStateAction<boolean>>;
};

export function CourseBuilderButtons({
    courseId,
    courseReadyToPublish,
    showPublishWarning,
    setShowPublishWarning,
    setShowPublishModal,
    setShowReviewModal,
}: CourseEditorButtonsProps): JSX.Element {
    const navigate = useNavigate();
    const { userData } = React.useContext(AuthContext);
    const { editMode, setShowIncompleteStepsWarning } = React.useContext(BuildCourseContext);
    const course = React.useContext(BuildCourseContext).course!;
    const snapshot = React.useContext(BuildCourseContext).snapshot!;

    const [exitEditModeWarning, setExitEditModeWarning] = React.useState(false);

    const hasChanges = React.useMemo(() => JSON.stringify(snapshot) !== JSON.stringify(course), [snapshot, course]);

    if (editMode) {
        return (
            <ButtonsContainer id="course-editor-buttons">
                <Button
                    title={strings.courseEditorPage.backToCourseButton.title}
                    aria={strings.courseEditorPage.backToCourseButton.aria}
                    icon={<LogoutIcon />}
                    onClick={() => {
                        hasChanges ? setExitEditModeWarning(true) : navigate(`/published-course/${course.id}`);
                    }}
                    fullwidth
                />
                <Button
                    title={strings.courseEditorPage.reviewChangesButton.title}
                    aria={strings.courseEditorPage.reviewChangesButton.aria}
                    colour='PRIMARY'
                    disabled={!hasChanges}
                    icon={<PreviewIcon size={IconSize.MEDIUM} colour={theme.primaryButtonTextColour} />}
                    onClick={() => setShowReviewModal(true)}
                    fullwidth
                />
                {exitEditModeWarning && (
                    <Alert
                        alertText={strings.courseEditorPage.discardChangesWarning.alertText}
                        buttonText={strings.courseEditorPage.discardChangesWarning.buttonText}
                        ariaButtonText={strings.courseEditorPage.discardChangesWarning.aria}
                        onSubmit={() => {
                            navigate(`/published-course/${course.id}`);
                        }}
                        closeAlert={() => {
                            setExitEditModeWarning(false)
                        }}
                    />
                )}
            </ButtonsContainer>
        );
    }

    return (
        <ButtonsContainer id="course-editor-buttons">
            {showPublishWarning && (
                <CourseBuilderValidationWarning
                    title={strings.courseEditorPage.stepsButtonValidation.title}
                    description={strings.courseEditorPage.stepsButtonValidation.description}
                    setShowPublishWarning={setShowPublishWarning}
                />
            )}

            <Button
                onClick={() => {
                    navigate(
                        userData.activeRole === RoleType.ORGANISATION_ADMIN
                            ? '/organisation/dashboard'
                            : '/trainer/dashboard',
                        { state: { activeTabId: 'buildCourses' } },
                    );
                }}
                title={strings.courseEditorPage.saveButtonTitle}
                aria={strings.courseEditorPage.saveButtonTitleAria}
                fullwidth
                icon={<LogoutIcon />}
            />

            <Button
                onClick={() => {
                    navigate(`/preview/${courseId}`, {
                        state: { previewContextEntryPoint: `/build-course/${courseId}` },
                    });
                }}
                title={strings.courseEditorPage.previewCourseButtonTitle}
                aria={strings.courseEditorPage.previewCourseButtonAria}
                icon={<VisibilityIcon colour={theme.primaryBrandColour} />}
                fullwidth
            />

            <Button
                onClick={() => {
                    if (courseReadyToPublish) {
                        setShowPublishModal(true);
                    } else {
                        setShowIncompleteStepsWarning(true);
                        setShowPublishWarning(true);
                    }
                }}
                title={strings.courseEditorPage.publishButtonTitle}
                aria={strings.courseEditorPage.publishButtonTitleAria}
                fullwidth
                colour='PRIMARY'
            />
        </ButtonsContainer>
    );
}

const ButtonsContainer = styled(FlexColMd)`
    padding-right: ${sizes.spacingMd};

    @media (max-width: ${breakpoints.sm}) {
        padding-right: unset;
    }
`;
